import { useCallback, useMemo, useState } from 'react';
import useInboxStore from '@/stores/InboxStore';
import { useHotkeys } from '@blueprintjs/core';
import { CheckIcon, PlusCircledIcon } from '@radix-ui/react-icons';
import { useShallow } from 'zustand/react/shallow';

import { trackTagFilterButtonClicked } from '@/lib/analytics-event';
import { cn } from '@/lib/utils';

import TooltipComponent from '../common/tooltip/tooltip';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '../ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';

const ConversationTagPicker = ({ disabled }) => {
  const { tags, selectedTags, setSelectedTags } = useInboxStore(
    useShallow((state) => ({
      tags: state.tags,
      selectedTags: state.selectedTags,
      setSelectedTags: state.setSelectedTags,
    }))
  );

  const addItem = (newItem) => {
    if (!disabled) {
      setSelectedTags([...selectedTags, newItem]);
    }
  };

  const removeItem = (itemToRemove) => {
    if (!disabled) {
      const updatedItems = selectedTags.filter((item) => item !== itemToRemove);
      setSelectedTags(updatedItems);
    }
  };

  const handleKeyPress = useCallback(() => {
    setFilterTagsPopoverOpen(true);
    trackTagFilterButtonClicked('keyboard');
  }, []);

  const hotkeys = useMemo(
    () => [
      {
        combo: 'shift + T',
        global: true,
        group: 'Filtering',
        // disabled: !contactSidebarShow,
        label: 'Filter Tag',
        onKeyUp: handleKeyPress,
      },
    ],
    [handleKeyPress]
  );
  const { handleKeyDown, handleKeyUp } = useHotkeys(hotkeys);

  const [filterTagsPopoverOpen, setFilterTagsPopoverOpen] = useState(false);

  return (
    <Popover
      open={filterTagsPopoverOpen}
      onOpenChange={setFilterTagsPopoverOpen}
    >
      <TooltipComponent
        content={
          <div className='flex space-x-2'>
            <div>Filter tag</div>
            <div className='flex space-x-1'>
              <kbd className='pointer-events-none  hidden h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100 sm:flex'>
                shift
              </kbd>
              <kbd className='pointer-events-none  hidden h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100 sm:flex'>
                T
              </kbd>
            </div>
          </div>
        }
      >
        <PopoverTrigger asChild onKeyDown={handleKeyDown} onKeyUp={handleKeyUp}>
          <Button
            variant='outline'
            size='sm'
            className='h-8 select-none border-dashed'
            onClick={() => trackTagFilterButtonClicked('mouse')}
            disabled={disabled}
          >
            <PlusCircledIcon className='mr-2 h-4 w-4' />
            Tag
            {selectedTags?.length > 0 && (
              <>
                <Separator orientation='vertical' className='mx-1 h-4' />
                <Badge
                  variant='secondary'
                  className='rounded-sm px-1 font-normal'
                >
                  {selectedTags.length}
                </Badge>
              </>
            )}
          </Button>
        </PopoverTrigger>
      </TooltipComponent>
      <PopoverContent
        className='w-[200px] p-0'
        align='start'
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        {tags.length > 0 ? (
          <Command>
            <CommandInput placeholder={'Tag'} />
            <CommandList>
              <CommandEmpty>No results found.</CommandEmpty>
              <CommandGroup>
                <ScrollArea className='h-[250px]'>
                  {tags.map((option) => {
                    const isSelected = selectedTags.find(
                      (element) => element === option._id
                    );
                    return (
                      <CommandItem
                        key={option._id}
                        onSelect={() => {
                          if (isSelected) {
                            removeItem(option._id);
                          } else {
                            addItem(option._id);
                          }
                        }}
                      >
                        <div
                          className={cn(
                            'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
                            isSelected
                              ? 'bg-primary text-primary-foreground'
                              : 'opacity-50 [&_svg]:invisible'
                          )}
                        >
                          <CheckIcon className={cn('h-4 w-4')} />
                        </div>
                        {option.icon && (
                          <option.icon className='mr-2 h-4 w-4 text-muted-foreground' />
                        )}
                        <span>{option.name}</span>
                      </CommandItem>
                    );
                  })}
                </ScrollArea>
              </CommandGroup>
              {selectedTags.length > 0 && (
                <>
                  <CommandSeparator />
                  <CommandGroup>
                    <CommandItem
                      onSelect={() => {
                        if (!disabled) {
                          setSelectedTags([]);
                        }
                      }}
                      className='justify-center text-center'
                    >
                      Clear Filter
                    </CommandItem>
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        ) : (
          <div className='py-6 text-center text-sm'> No Tags</div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default ConversationTagPicker;
